import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useNavigate, useLocation } from "react-router-dom";

export const Navbar = ({
  scrolled,
  isMenuOpen,
  setIsMenuOpen,
  scrollToSection,
  setShowQuoteForm,
  lang,
  toggleLang,
  t
}) => {

  const navigate = useNavigate();
  const location = useLocation();

  const goHome = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection("home"), 100);
    } else {
      scrollToSection("home");
    }
    setIsMenuOpen(false);
  };

  // Handle normal scroll sections (Home, Why Us, etc.)
  const handleScrollNavigation = (item) => {
    const sectionMap = {
      'الرئيسية': 'home',
      'Home': 'home',
      'لماذا نحن': 'whyus',
      'Why Us': 'whyus',
      'فريقنا': 'ourteam',
      'Our Team': 'ourteam',
      'تواصل معنا': 'contact',
      'Contact': 'contact'
    };

    const sectionId = sectionMap[item];

    if (sectionId) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToSection(sectionId), 200);
      } else {
        scrollToSection(sectionId);
      }
      setIsMenuOpen(false);
    }
  };

  // Special: Services → Go to /all-services page
  const handleServicesClick = () => {
    navigate("/all-services");
    setIsMenuOpen(false);
  };

  // Special: Clients → Go to /clients page
  const handleClientsNavigation = () => {
    navigate("/clients");
    setIsMenuOpen(false);
  };

  // Get the "Services" text in current language
  const servicesText = lang === 'ar' ? 'الخدمات' : 'Services';

  return (
    <header
      className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl shadow-xl"
          : "bg-white/80 backdrop-blur-md"
      }`}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">

          {/* LOGO → GO HOME */}
          <button onClick={goHome} className="flex items-center gap-2 group cursor-pointer">
            <motion.div
              className="w-12 h-12 bg-gradient-to-br rounded-xl flex items-center justify-center group-hover:shadow-lg"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.3 }}
            >
              <img className="w-12 h-12" src="/logo1.png" alt="Saturn Lynk Logo" />
            </motion.div>
          </button>

          {/* DESKTOP MENU */}
          <nav className="hidden lg:flex items-center space-x-1 space-x-reverse">
            {t("nav").map((item) => {
              if (item === servicesText) {
                // Special handling for Services
                return (
                  <button
                    key={item}
                    onClick={handleServicesClick}
                    className="px-4 py-2 text-gray-700 hover:text-blue-500 font-medium transition-all rounded-lg hover:bg-blue-50/50"
                  >
                    {item}
                  </button>
                );
              }

              // All other nav items → scroll
              return (
                <button
                  key={item}
                  onClick={() => handleScrollNavigation(item)}
                  className="px-4 py-2 text-gray-700 hover:text-blue-500 font-medium transition-all rounded-lg hover:bg-blue-50/50"
                >
                  {item}
                </button>
              );
            })}

            {/* Clients Link */}
            <button
              onClick={handleClientsNavigation}
              className="px-4 py-2 text-gray-700 hover:text-blue-500 font-medium transition-all rounded-lg hover:bg-blue-50/50"
            >
              {t("clients")}
            </button>
          </nav>

          {/* RIGHT SIDE BUTTONS */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher lang={lang} toggleLang={toggleLang} />
            <button
              onClick={() => setShowQuoteForm(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-400 to-cyan-300 text-white rounded-lg font-semibold hover:shadow-md transition-all duration-300 flex items-center gap-1.5"
            >
              {t("freeConsultation")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* MOBILE MENU TOGGLE */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSwitcher lang={lang} toggleLang={toggleLang} />
            <button className="p-1 hover:bg-gray-100 rounded-lg" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden bg-white border-t"
            dir={lang === "ar" ? "rtl" : "ltr"}
          >
            <nav className="px-4 py-4 space-y-2">
              {t("nav").map((item) => {
                if (item === servicesText) {
                  return (
                    <button
                      key={item}
                      onClick={() => {
                        handleServicesClick();
                      }}
                      className="block w-full text-left px-3 py-2 text-gray-700 hover:text-blue-500 hover:bg-blue-50 rounded-lg font-medium"
                    >
                      {item}
                    </button>
                  );
                }

                return (
                  <button
                    key={item}
                    onClick={() => {
                      handleScrollNavigation(item);
                    }}
                    className="block w-full text-left px-3 py-2 text-gray-700 hover:text-blue-500 hover:bg-blue-50 rounded-lg font-medium"
                  >
                    {item}
                  </button>
                );
              })}

              {/* Clients in Mobile */}
              <button
                onClick={handleClientsNavigation}
                className="block w-full text-left px-3 py-2 text-gray-700 hover:text-blue-500 hover:bg-blue-50 rounded-lg font-medium"
              >
                {t("clients")}
              </button>

              <button
                onClick={() => {
                  setShowQuoteForm(true);
                  setIsMenuOpen(false);
                }}
                className="w-full mt-3 px-5 py-3 bg-gradient-to-r from-blue-400 to-cyan-300 text-white rounded-lg font-semibold shadow-sm"
              >
                {t("freeConsultation")}
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};